import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { appConfig } from '../../config/configManager';
import { useProfileStore } from '../../contexts/StoreContext';
import { StoredConcernData, CONCERN_STORAGE_KEY, deriveConcernDomain } from '../../types/concerns';
import { ConcernState } from '../../types/profile';
import { checkDomainStaleness, loadConcernFreshnessThreshold, loadShowLastUpdateSetting, formatComputationTimestamp } from '../../utils/milestoneStaleness';
import { ActionDefStorage, ActionDefDto, ActionMVarDto } from '../../storage/ActionDefStorage';
import type { AdminActionDto } from '../../types/api/appProfile';
import type { NavigationTab } from '../../types/navigation';
import { ActionPlanSummary } from './ActionPlanSummary';
import { PrescriptionListEditor } from './PrescriptionListEditor';
import type { PrescriptionListEditorHandle } from './PrescriptionListEditor';
import { ReferralListEditor } from './ReferralListEditor';
import type { ReferralListEditorHandle } from './ReferralListEditor';
import type { PrescriptionEventDto, ReferralEventDto } from '../../storage/WellCheckStorage';
import { syncMedicalTrackActions } from '../../utils/medicalTrackSync';
import DatePickerModal from '../DatePickerModal';

type ActionTab = 'Goals' | 'Provider' | 'Manage' | 'Advocate' | 'Summary';

interface TabInfo {
  key: ActionTab;
  name: string;
  headerTitle?: string;  // Longer title shown in the domain header (falls back to name)
  icon: string;
  description: string;
}

const ACTION_TABS: TabInfo[] = [
  { key: 'Goals',    name: 'Goals',    headerTitle: 'Select Goals Actions',    icon: '⭐', description: 'Developmental goals and targets' },
  { key: 'Manage',   name: 'Concerns', headerTitle: 'Select Concerns Actions',  icon: '🛠️', description: 'Active concerns to address' },
  { key: 'Advocate', name: 'Advocate', headerTitle: 'Select Advocate Actions', icon: '🤝', description: 'Advocacy steps and resources' },
  { key: 'Provider', name: 'Provider', headerTitle: 'Select Provider Actions', icon: '🩺', description: 'Actions for healthcare providers' },
  { key: 'Summary',  name: 'Action Plan', icon: '📋', description: 'Overview of all action items' },
];

/** Map ActionDef group id → ActionPlan display info */
const GROUP_TAB_INFO: Record<string, { icon: string; name: string }> = {
  '1': { icon: '⭐', name: 'Goals' },
  '2': { icon: '🛠️', name: 'Concerns' },
  '3': { icon: '🤝', name: 'Advocate' },
  '4': { icon: '🩺', name: 'Provider' },
};

/** Map ActionDef group id → ACTION_TABS index for navigation */
const GROUP_TAB_INDEX: Record<string, number> = {
  '1': 0, // Goals
  '2': 1, // Manage / Concerns
  '3': 2, // Advocate
  '4': 3, // Provider
};

interface ActionsViewProps {
  initialGroup?: string;
  onNavigateWithDomain?: (tab: NavigationTab, domain?: string) => void;
  showBackToSnapshot?: boolean;
  onBackToSnapshot?: () => void;
  onHomeDashboard?: () => void;
}

/** Normalise legacy blank / 'pending' status values to '1' (in-process). */
function normaliseStatus(s: string | undefined): string | null {
  return (!s || s === 'pending') ? '1' : s;
}

export const ActionsView: React.FC<ActionsViewProps> = ({ initialGroup, onNavigateWithDomain, showBackToSnapshot, onBackToSnapshot, onHomeDashboard }) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    initialGroup != null ? (GROUP_TAB_INDEX[initialGroup] ?? 0) : 0
  );
  /** Key of the action currently awaiting outcome selection: `group|mvar|action` */
  const [completingAction, setCompletingAction] = useState<string | null>(null);
  const [datePickerTarget, setDatePickerTarget] = useState<{ group: string; mvarid: string; actionId: string } | null>(null);

  const selectedTab = ACTION_TABS[selectedTabIndex];

  // ── Concern data ─────────────────────────────────────────────────────────
  const profileStore = useProfileStore();
  const { getActiveProfile, updateProfile, markProfileDirty, syncProfileToBackend } = profileStore();
  const activeProfile = getActiveProfile();

  const [concernData, setConcernData] = useState<StoredConcernData | null>(null);
  const [freshnessThreshold, setFreshnessThreshold] = useState('2weeks');
  const [showLastUpdate, setShowLastUpdate] = useState(true);

  // ── ActionDef — definition catalogue for available actions per mvar ──────
  const [actionDef, setActionDef] = useState<ActionDefDto | null>(null);

  // ── Track Prescriptions / Referrals settings ─────────────────────────────
  const [trackPrescriptions, setTrackPrescriptions] = useState(true);
  const [trackReferrals, setTrackReferrals] = useState(true);

  // Live actionsstate from profile (AdminActionDto instances already chosen by the user)
  const actionsState: AdminActionDto[] = activeProfile?.actionsstate ?? [];

  const loadConcernData = useCallback(async () => {
    const storage = (globalThis as any).__childNavStorage ?? (globalThis as any).__childNavStorage ?? null;
    if (!storage) return;
    try {
      const raw = await storage.getItem(CONCERN_STORAGE_KEY);
      if (raw) {
        const parsed: StoredConcernData = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setConcernData(parsed);
      }
      const freshness = await loadConcernFreshnessThreshold(storage);
      setFreshnessThreshold(freshness);
      const showUpdate = await loadShowLastUpdateSetting(storage);
      setShowLastUpdate(showUpdate);
      // Load ActionDef catalogue (definitions of available actions per mvar)
      const actionDefStorage = new ActionDefStorage(storage);
      const def = await actionDefStorage.getActionDef();
      setActionDef(def);
      const savedTrackRx = await storage.getItem('wellcheck_track_prescriptions');
      if (savedTrackRx !== null) setTrackPrescriptions(savedTrackRx === 'true');
      const savedTrackRef = await storage.getItem('wellcheck_track_referrals');
      if (savedTrackRef !== null) setTrackReferrals(savedTrackRef === 'true');
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadConcernData(); }, [loadConcernData]);

  // ── Tab last-updated timestamp ────────────────────────────────────────────
  // For Manage/Advocate we also consider the most recent lastUpdated among the
  // filtered concern states (action===2 / action===3) in addition to the sentinel.
  const tabLastUpdated = useMemo<string | null>(() => {
    const states: ConcernState[] = activeProfile?.concernsstate ?? [];
    const sentinelKey = `action_tab_viewed_${selectedTab.key.toLowerCase()}`;
    const sentinel = states.find(c => c.mvarid === sentinelKey);
    const timestamps: string[] = [];
    if (sentinel?.lastUpdated) timestamps.push(sentinel.lastUpdated);
    if (selectedTab.key === 'Manage') {
      // Manage tab shows both action=2 and action=3 concerns
      states.filter(c => (c.action === 2 || c.action === 3) && c.lastUpdated).forEach(c => timestamps.push(c.lastUpdated));
    } else if (selectedTab.key === 'Advocate') {
      states.filter(c => c.action === 3 && c.lastUpdated).forEach(c => timestamps.push(c.lastUpdated));
    }
    return timestamps.length > 0 ? timestamps.reduce((a, b) => (a > b ? a : b)) : null;
  }, [activeProfile, selectedTab.key]);

  const tabStaleness = useMemo(
    () => checkDomainStaleness(tabLastUpdated, freshnessThreshold),
    [tabLastUpdated, freshnessThreshold]
  );

  // Stamp the tab being left so we have a "last reviewed" timestamp
  const markTabViewed = useCallback((tabKey: string) => {
    if (!activeProfile) return;
    const sentinelId = `action_tab_viewed_${tabKey.toLowerCase()}`;
    const now = new Date().toISOString();
    const existing: ConcernState[] = activeProfile.concernsstate ?? [];
    const idx = existing.findIndex(c => c.mvarid === sentinelId);
    const updated = idx >= 0
      ? [...existing.slice(0, idx), { ...existing[idx], lastUpdated: now }, ...existing.slice(idx + 1)]
      : [...existing, { mvarid: sentinelId, state: 'no' as const, lastUpdated: now, notes: '' }];
    updateProfile(activeProfile.id, { concernsstate: updated });
    markProfileDirty(activeProfile.id, `Action tab viewed: ${tabKey}`);
  }, [activeProfile, updateProfile, markProfileDirty]);

  // Build mvarid → concern domain key lookup (for navigation)
  const mvaridDomainMap = useMemo(() => {
    const map = new Map<string, string>();
    if (concernData) {
      for (const group of concernData.groups) {
        for (const item of group.items) {
          const key = item.mvarName ?? `${group.mvarName.toLowerCase()}_${item.keyValue}`;
          map.set(key, group.domainKey);
        }
      }
    }
    // Measures concerns map to the 'measures' domain
    const MEASURES_KEYS = ['cn_lc_measure','cn_mt_measure','cn_fm_measure',
      'cn_gm_measure','cn_cg_measure','cn_se_measure',
      'cn_inflex_measure','cn_irrit_measure','cn_rout_measure','cn_posi_measure','cn_ppsc_measure'];
    for (const k of MEASURES_KEYS) map.set(k, 'measures');
    return map;
  }, [concernData]);

  const getConcernDomainKey = useCallback((mvarid: string): string => {
    if (mvaridDomainMap.has(mvarid)) return mvaridDomainMap.get(mvarid)!;
    // Fallback: derive from the mvarName prefix (everything before the last _digit segment)
    const mvarName = mvarid.replace(/_[^_]+$/, '');
    return deriveConcernDomain(mvarName);
  }, [mvaridDomainMap]);

  // Build mvarid → display text lookup
  const concernTextMap = useMemo(() => {
    const map = new Map<string, string>();
    // AdminSegment 11272 concerns: {mvarName}_{keyValue} → item display text
    if (concernData) {
      for (const group of concernData.groups) {
        for (const item of group.items) {
          const key = item.mvarName ?? `${group.mvarName.toLowerCase()}_${item.keyValue}`;
          map.set(key, item.text);
        }
      }
    }
    // Measures domain concerns: cn_{code}_measure → readable label
    const MEASURES_TEXT: Record<string, string> = {
      'cn_lc_measure':     '💬 Communication – Developmental Age Concern',
      'cn_mt_measure':     '🏃 Motor Skills – Developmental Age Concern',
      'cn_fm_measure':     '✋ Fine Motor – Developmental Age Concern',
      'cn_gm_measure':     '🤸 Gross Motor – Developmental Age Concern',
      'cn_cg_measure':     '🧠 Cognitive – Developmental Age Concern',
      'cn_se_measure':     '😊 Social-Emotional – Developmental Age Concern',
      'cn_inflex_measure': '🤯 Inflexibility (BPSC) Concern',
      'cn_irrit_measure':  '😠 Irritability (BPSC) Concern',
      'cn_rout_measure':   '🔄 Routine Problems (BPSC) Concern',
      'cn_posi_measure':   '👨‍👩‍👧 Parent Stress (POSI) Concern',
      'cn_ppsc_measure':   '🧠 Parent Psych. (PPSC) Concern',
    };
    for (const [k, v] of Object.entries(MEASURES_TEXT)) map.set(k, v);
    return map;
  }, [concernData]);

  // Filtered concern lists by action value
  const manageConcerns = useMemo(() => {
    const states: ConcernState[] = activeProfile?.concernsstate ?? [];
    return states
      .filter(c => c.action === 2)
      .map(c => ({ mvarid: c.mvarid, text: concernTextMap.get(c.mvarid) ?? c.mvarid }));
  }, [activeProfile, concernTextMap]);

  const seekHelpConcerns = useMemo(() => {
    const states: ConcernState[] = activeProfile?.concernsstate ?? [];
    return states
      .filter(c => c.action === 3)
      .map(c => ({ mvarid: c.mvarid, text: concernTextMap.get(c.mvarid) ?? c.mvarid }));
  }, [activeProfile, concernTextMap]);

  // ── ActionDef helpers ──────────────────────────────────────────────────────

  /**
   * Return the ActionMVarDto for the given ActionDef group and concern mvarid.
   * Returns null when ActionDef hasn't loaded or the mvar has no entry for that group.
   */
  const getGroupMVar = useCallback((group: string, mvarid: string): ActionMVarDto | null => {
    if (!actionDef) return null;
    const g = actionDef.actionGroups.find(ag => ag.group === group);
    if (!g) return null;
    return g.actionMVars.find(m => m.mvar.toLowerCase() === mvarid.toLowerCase()) ?? null;
  }, [actionDef]);

  /**
   * Return all ActionMVarDtos defined for a group (used by Goals and Provider
   * which display every mvar regardless of selected concerns).
   */
  const getGroupAllMVars = useCallback((group: string): ActionMVarDto[] => {
    if (!actionDef) return [];
    return actionDef.actionGroups.find(ag => ag.group === group)?.actionMVars ?? [];
  }, [actionDef]);

  /** Return the ActionTypeDto for a given action identifier. */
  const getActionType = useCallback((actionId: string) => {
    if (!actionDef) return null;
    return actionDef.actionTypes.find(t => t.action.toLowerCase() === actionId.toLowerCase()) ?? null;
  }, [actionDef]);

  /**
   * Returns true when the action uses a specific date (dueDate) rather than a
   * recurring schedule interval.  Detects:
   *  - legacy numeric id '1'
   *  - ActionTypeDto.name === 'event' (case-insensitive)
   *  - single schedule option whose text contains 'date' (backend placeholder)
   */
  const isDateEventType = useCallback((actionId: string): boolean => {
    if (actionId === '1') return true;
    const at = actionDef?.actionTypes.find(t => t.action.toLowerCase() === actionId.toLowerCase());
    if (!at) return false;
    if (at.name.toLowerCase() === 'event') return true;
    if (at.schedule.length === 1 && at.schedule[0].text.toLowerCase().includes('date')) return true;
    return false;
  }, [actionDef]);

  const formatDateValue = useCallback((value: string): string => {
    if (!value) return 'Select date';
    const parts = value.split('-');
    if (parts.length !== 3) return value;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!year || !month || !day) return value;
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }, []);

  const openDatePicker = useCallback((group: string, mvarid: string, actionId: string) => {
    setDatePickerTarget({ group, mvarid, actionId });
  }, []);

  const closeDatePicker = useCallback(() => {
    setDatePickerTarget(null);
  }, []);

  /** Return the display name for an actionId (falls back to the raw identifier). */
  const getActionName = useCallback((actionId: string): string => {
    if (!actionDef) return actionId;
    return actionDef.actionTypes.find(t => t.action.toLowerCase() === actionId.toLowerCase())?.name ?? actionId;
  }, [actionDef]);

  /**
   * Inline helper links for specific action types.
   * - type 3: "Learn more" -> Guidance
   * - type 12: "Find resources" -> Resource list
   */
  const getActionQuickLink = useCallback((actionId: string): { label: string; tab: NavigationTab } | null => {
    const key = String(actionId).trim().toLowerCase();
    if (key === '3') return { label: 'Check Guidance', tab: 'Guidance' };
    if (key === '12') return { label: 'Check Resource List', tab: 'ResourceList' };
    return null;
  }, []);

  /** Return the live AdminActionDto instance for (group, mvarid, actionId), or undefined. */
  const getAdminAction = useCallback((group: string, mvarid: string, actionId: string): AdminActionDto | undefined => {
    return actionsState.find(
      a => a.group === group &&
           a.mvar.toLowerCase() === mvarid.toLowerCase() &&
           a.action.toLowerCase() === actionId.toLowerCase()
    );
  }, [actionsState]);

  /** Patch a single field on a matching AdminActionDto in actionsstate. */
  const updateActionField = useCallback((
    group: string, mvarid: string, actionId: string,
    field: keyof AdminActionDto, value: string
  ) => {
    if (!activeProfile) return;
    const current: AdminActionDto[] = activeProfile.actionsstate ?? [];
    const updated = current.map(a =>
      a.group === group &&
      a.mvar.toLowerCase() === mvarid.toLowerCase() &&
      a.action.toLowerCase() === actionId.toLowerCase()
        ? { ...a, [field]: value }
        : a
    );
    updateProfile(activeProfile.id, { actionsstate: updated });
    markProfileDirty(activeProfile.id, `Action field updated: group=${group} mvar=${mvarid} action=${actionId} ${String(field)}=${value}`);
  }, [activeProfile, updateProfile, markProfileDirty]);

  const pickerInitialValue = useMemo(() => {
    if (!datePickerTarget) return '';
    const current = getAdminAction(datePickerTarget.group, datePickerTarget.mvarid, datePickerTarget.actionId);
    return current?.dueDate ?? '';
  }, [datePickerTarget, getAdminAction]);

  /**
   * Move an action up or down in global rank order.
   * Items without a rank are treated as lowest-priority (appended after ranked items).
   */
  const swapActionRanks = useCallback((
    groupA: string, mvarA: string, actionA: string,
    groupB: string, mvarB: string, actionB: string,
  ) => {
    if (!activeProfile) return;
    const current: AdminActionDto[] = activeProfile.actionsstate ?? [];
    if (current.length < 2) return;
    // Assign normalised integer ranks (preserving insertion order for unranked)
    const withRanks = current.map((a, i) => ({ ...a, rank: a.rank ?? (current.length + i + 1) }));
    withRanks.sort((a, b) => (a.rank as number) - (b.rank as number));
    // Re-number so ranks are 1..N
    withRanks.forEach((a, i) => { a.rank = i + 1; });
    const idxA = withRanks.findIndex(
      a => a.group === groupA &&
           a.mvar.toLowerCase() === mvarA.toLowerCase() &&
           a.action.toLowerCase() === actionA.toLowerCase()
    );
    const idxB = withRanks.findIndex(
      a => a.group === groupB &&
           a.mvar.toLowerCase() === mvarB.toLowerCase() &&
           a.action.toLowerCase() === actionB.toLowerCase()
    );
    if (idxA < 0 || idxB < 0) return;
    // Swap ranks
    const tmp = withRanks[idxA].rank as number;
    withRanks[idxA].rank = withRanks[idxB].rank;
    withRanks[idxB].rank = tmp;
    updateProfile(activeProfile.id, { actionsstate: withRanks });
    markProfileDirty(activeProfile.id, `Action rank swapped: ${groupA}/${mvarA}/${actionA} ↔ ${groupB}/${mvarB}/${actionB}`);
  }, [activeProfile, updateProfile, markProfileDirty]);

  /**
   * Common inline schedule selector — always rendered on the same row as the action
   * checkbox. Grayed and non-interactive when the action is not yet checked.
   * - actionId === '3' (Schedule Event): compact date input.
   * - All others: cycle through ActionTypeDto.schedule items with a › arrow.
   * Returns null only when the action type defines no schedule options.
   */
  const renderScheduleSelector = useCallback((
    group: string, mvarid: string, actionId: string, enabled: boolean
  ) => {
    const actionType = getActionType(actionId);
    const instance   = getAdminAction(group, mvarid, actionId);
    const isDateEvent = isDateEventType(actionId);

    if (isDateEvent) {
      const dateValue = (enabled && instance) ? (instance.dueDate ?? '') : '';
      return (
        <View style={[styles.scheduleInline, !enabled && styles.scheduleInlineDisabled]}>
          {Platform.OS === 'web' ? (
            // @ts-ignore — web-only HTML element
            <input
              type="date"
              value={dateValue}
              disabled={!enabled}
              onChange={(e: any) => {
                if (enabled) updateActionField(group, mvarid, actionId, 'dueDate', e.target.value);
              }}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: 14,
                color: enabled ? '#1e293b' : '#94a3b8',
                outline: 'none',
                width: '100%',
                whiteSpace: 'nowrap',
                cursor: enabled ? 'pointer' : 'default',
              } as any}
            />
          ) : (
            <TouchableOpacity
              style={[styles.scheduleDateButton, !enabled && styles.scheduleDateButtonDisabled]}
              onPress={() => { if (enabled) openDatePicker(group, mvarid, actionId); }}
              activeOpacity={enabled ? 0.75 : 1}
              disabled={!enabled}
            >
              <Text style={[styles.scheduleDateButtonText, !enabled && styles.scheduleDateButtonTextDisabled]} numberOfLines={1}>
                {dateValue ? formatDateValue(dateValue) : 'Select date'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }

    // Cycle selector
    const options = actionType?.schedule ?? [];
    if (options.length === 0) return null;
    const currentValue = (enabled && instance) ? (instance.schedule ?? '') : '';
    const currentIdx   = options.findIndex(o => o.value === currentValue);
    const displayText  = currentIdx >= 0 ? options[currentIdx].text : (options[0]?.text ?? '—');

    const cycleNext = () => {
      if (!enabled) return;
      const nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % options.length;
      updateActionField(group, mvarid, actionId, 'schedule', options[nextIdx].value);
    };

    return (
      <TouchableOpacity
        style={[styles.scheduleSelectorBtn, !enabled && styles.scheduleSelectorBtnDisabled]}
        onPress={cycleNext}
        activeOpacity={enabled ? 0.7 : 1}
      >
        <Text style={[styles.scheduleValue, !enabled && styles.scheduleValueDisabled]} numberOfLines={1}>
          {displayText}
        </Text>
        <Text style={[styles.scheduleArrow, !enabled && styles.scheduleArrowDisabled]}>›</Text>
      </TouchableOpacity>
    );
  }, [getActionType, getAdminAction, isDateEventType, updateActionField]);

  /** True when an AdminActionDto for (group, mvar, action) exists and is actively in-process (status='1'). */
  const isActionEnabled = useCallback((group: string, mvarid: string, actionId: string): boolean => {
    const instance = actionsState.find(
      a => a.group === group &&
           a.mvar.toLowerCase() === mvarid.toLowerCase() &&
           a.action.toLowerCase() === actionId.toLowerCase()
    );
    return !!instance && normaliseStatus(instance.status) === '1';
  }, [actionsState]);

  /**
   * Toggle an AdminActionDto on/off.
   * - No record  → create with status='1' (in-process)
   * - status='1' → set status='0' (deselected, awaiting outcome or deletion)
   * - status='0' → set status='1' (re-enable)
   * - status='-1' → no-op (finished actions are read-only)
   */
  const toggleAction = useCallback((group: string, mvarid: string, actionId: string) => {
    if (!activeProfile) return;
    const current: AdminActionDto[] = activeProfile.actionsstate ?? [];
    const matchKey = (a: AdminActionDto) =>
      a.group === group &&
      a.mvar.toLowerCase() === mvarid.toLowerCase() &&
      a.action.toLowerCase() === actionId.toLowerCase();
    const existing = current.find(matchKey);
    const defaultSchedule = isDateEventType(actionId)
      ? ''
      : (getActionType(actionId)?.default ?? '');
    let updated: AdminActionDto[];
    let logMsg: string;
    if (!existing) {
      updated = [
        ...current,
        {
          source:     actionDef?.source ?? '',
          group,
          mvar:       mvarid,
          action:     actionId,
          dueDate:    '',
          location:   '',
          schedule:   defaultSchedule,
          actual:     '',
          expense:    '',
          importance: '',
          outcome:    '',
          created:    new Date().toISOString(),
          completed:  '',
          status:     '1',
        } as AdminActionDto,
      ];
      logMsg = `Action added: group=${group} mvar=${mvarid} action=${actionId}`;
    } else if (normaliseStatus(existing.status) === '1') {
      // Deselect — also stamps status='0' on any legacy blank/'pending' records
      updated = current.map(a => matchKey(a) ? { ...a, status: '0' } : a);
      logMsg = `Action deselected: group=${group} mvar=${mvarid} action=${actionId}`;
    } else if (existing.status === '0') {
      updated = current.map(a => matchKey(a) ? { ...a, status: '1' } : a);
      logMsg = `Action re-enabled: group=${group} mvar=${mvarid} action=${actionId}`;
    } else if (existing.status === '-1') {
      // Finished — tap reactivates as a fresh in-process selection
      updated = current.map(a => matchKey(a) ? { ...a, status: '1', outcome: '', completed: '' } : a);
      logMsg = `Action reactivated: group=${group} mvar=${mvarid} action=${actionId}`;
    } else {
      return; // unknown status — no-op
    }
    updateProfile(activeProfile.id, { actionsstate: updated });
    markProfileDirty(activeProfile.id, logMsg);
  }, [activeProfile, actionDef, getActionType, isDateEventType, updateProfile, markProfileDirty]);

  /** Delete an AdminActionDto record entirely. */
  const deleteAction = useCallback((group: string, mvarid: string, actionId: string) => {
    if (!activeProfile) return;
    const current: AdminActionDto[] = activeProfile.actionsstate ?? [];
    const updated = current.filter(
      a => !(a.group === group &&
             a.mvar.toLowerCase() === mvarid.toLowerCase() &&
             a.action.toLowerCase() === actionId.toLowerCase())
    );
    setCompletingAction(null);
    updateProfile(activeProfile.id, { actionsstate: updated });
    markProfileDirty(activeProfile.id, `Action deleted: group=${group} mvar=${mvarid} action=${actionId}`);
  }, [activeProfile, updateProfile, markProfileDirty]);

  /** Mark an action as finished: record outcome + completed timestamp + status='-1'. */
  const markActionComplete = useCallback((
    group: string, mvarid: string, actionId: string, outcomeValue: string
  ) => {
    if (!activeProfile) return;
    const current: AdminActionDto[] = activeProfile.actionsstate ?? [];
    const now = new Date().toISOString();
    const updated = current.map(a =>
      a.group === group &&
      a.mvar.toLowerCase() === mvarid.toLowerCase() &&
      a.action.toLowerCase() === actionId.toLowerCase()
        ? { ...a, status: '-1', outcome: outcomeValue, completed: now }
        : a
    );
    setCompletingAction(null);
    updateProfile(activeProfile.id, { actionsstate: updated });
    markProfileDirty(activeProfile.id, `Action completed: group=${group} mvar=${mvarid} action=${actionId}`);
  }, [activeProfile, updateProfile, markProfileDirty]);

  /**
   * Render action checkboxes for a concern card.
   * Each row: [checkbox + label (flex:1)] [inline schedule selector].
   * The selector is always shown but grayed when the action is unchecked.
   */
  const renderActionCheckboxes = useCallback((group: string, mvarid: string) => {
    const mvarEntry = getGroupMVar(group, mvarid);
    if (!mvarEntry || mvarEntry.actions.length === 0) return null;
    return (
      <View style={styles.actionCheckList}>
        {mvarEntry.actions.map(actionId => {
          const instance = getAdminAction(group, mvarid, actionId);
          // null = no record (unchecked); normalise blank/'pending' only for existing instances
          const status   = instance ? normaliseStatus(instance.status) : null;
          // status='-1' (finished) renders as unchecked — same as no instance, tap reactivates
          const label = getActionName(actionId);
          const ck    = `${group}|${mvarid}|${actionId}`;
          if (status === '0') {
            const at = getActionType(actionId);
            const isCompletingThis = completingAction === ck;
            return (
              <View key={actionId} style={styles.actionCheckRow}>
                <View style={styles.deselectedBlock}>
                  <Text style={styles.deselectedLabel}>{label}</Text>
                  <View style={styles.completionBtnsRow}>
                    <TouchableOpacity style={styles.completionMarkBtn} onPress={() => setCompletingAction(isCompletingThis ? null : ck)} activeOpacity={0.75}>
                      <Text style={styles.completionMarkBtnText}>Mark complete</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.completionDeleteBtn} onPress={() => deleteAction(group, mvarid, actionId)} activeOpacity={0.75}>
                      <Text style={styles.completionDeleteBtnText}>Delete</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.completionReEnableBtn} onPress={() => toggleAction(group, mvarid, actionId)} activeOpacity={0.75}>
                      <Text style={styles.completionReEnableBtnText}>Re-enable</Text>
                    </TouchableOpacity>
                  </View>
                  {isCompletingThis && at && at.outcome.length > 0 && (
                    <View style={styles.outcomeRow}>
                      {at.outcome.map(o => (
                        <TouchableOpacity key={o.value} style={styles.outcomeBtn} onPress={() => markActionComplete(group, mvarid, actionId, o.value)} activeOpacity={0.75}>
                          <Text style={styles.outcomeBtnText}>{o.text}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                  {isCompletingThis && at && at.outcome.length === 0 && (
                    <TouchableOpacity style={styles.outcomeBtn} onPress={() => markActionComplete(group, mvarid, actionId, '')} activeOpacity={0.75}>
                      <Text style={styles.outcomeBtnText}>Confirm complete</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            );
          }
          // Active (status='1') or not yet selected
          const enabled  = status === '1';
          const quickLink = getActionQuickLink(actionId);
          const selector = renderScheduleSelector(group, mvarid, actionId, enabled);
          return (
            <View key={actionId} style={styles.actionCheckRow}>
              {/* Checkbox + label — tap to toggle */}
              <TouchableOpacity
                style={styles.actionCheckTouchable}
                onPress={() => toggleAction(group, mvarid, actionId)}
                activeOpacity={0.7}
              >
                <View style={[styles.actionCheckBox, enabled && styles.actionCheckBoxChecked]}>
                  {enabled && <Text style={styles.actionCheckMark}>✓</Text>}
                </View>
                <Text style={[styles.actionCheckLabel, enabled && styles.actionCheckLabelEnabled]}>
                  {label}
                </Text>
              </TouchableOpacity>
              {/* Inline schedule selector — always shown, grayed when unchecked */}
              {selector}

              {quickLink && (
                <TouchableOpacity
                  style={styles.actionQuickLinkBtn}
                  onPress={() => onNavigateWithDomain?.(quickLink.tab)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.actionQuickLinkText}>{quickLink.label}</Text>
                </TouchableOpacity>
              )}
            </View>
          );
        })}
      </View>
    );
  }, [getGroupMVar, getAdminAction, getActionType, isActionEnabled, getActionName, getActionQuickLink, toggleAction, deleteAction, markActionComplete, renderScheduleSelector, completingAction, onNavigateWithDomain]);

  // ── Tab navigation ─────────────────────────────────────────────────────────

  const navigateToTab = useCallback((nextIndex: number) => {
    // Stamp the tab we're leaving
    const leavingKey = ACTION_TABS[selectedTabIndex]?.key;
    if (leavingKey) markTabViewed(leavingKey);
    setSelectedTabIndex(nextIndex);
  }, [selectedTabIndex, markTabViewed]);

  const handleHomeDashboard = useCallback(() => {
    markTabViewed(selectedTab.key);
    onHomeDashboard?.();
  }, [selectedTab, markTabViewed, onHomeDashboard]);

  const handlePrevTab = () => {
    if (selectedTabIndex > 0) navigateToTab(selectedTabIndex - 1);
  };

  const handleNextTab = () => {
    if (selectedTabIndex < ACTION_TABS.length - 1) navigateToTab(selectedTabIndex + 1);
  };

  const renderSummaryContent = () => (
    <ActionPlanSummary
      actionsState={actionsState}
      actionDef={actionDef}
      prescriptionEvents={activeProfile?.prescriptiontrack?.events ?? []}
      referralEvents={activeProfile?.referraltrack?.events ?? []}
      showRankBtns={true}
      onGroupPress={(group, item) => {
        const mvar = (item?.mvar || '').toLowerCase();
        if (mvar.startsWith('wellcheck_')) {
          // Cross-view handoff to Well-Visit -> Schedule.
          onNavigateWithDomain?.('Well-Visit', 'Schedule');
          return;
        }
        navigateToTab(GROUP_TAB_INDEX[group] ?? 0);
      }}
      onRankSwap={swapActionRanks}
    />
  );

  const renderGoalsContent = () => {
    const mvars = getGroupAllMVars('1');
    return (
      <View style={styles.section}>
        {mvars.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No goal actions defined yet.</Text>
          </View>
        ) : (
          mvars.map(m => (
            <View key={m.mvar} style={[styles.actionCard, { borderLeftColor: '#f59e0b' }]}>
              <Text style={styles.actionTitle}>⭐ {m.text || m.mvar}</Text>
              {renderActionCheckboxes('1', m.mvar)}
            </View>
          ))
        )}
      </View>
    );
  };

  const renderManageContent = () => {
    // Manage shows ALL selected concerns — both action=2 (Manage) and action=3 (Seek Help)
    const allManageConcerns = [...manageConcerns, ...seekHelpConcerns];
    // Orphaned: actions for group '2' whose mvarid is NOT in current concern list
    const orphanedManage = actionsState.filter(a =>
      a.group === '2' &&
      a.status !== '-1' &&
      !allManageConcerns.some(c => c.mvarid.toLowerCase() === a.mvar.toLowerCase())
    );
    return (
      <View style={styles.section}>
        {allManageConcerns.length === 0 && orphanedManage.length === 0 && (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No concerns found. Visit the Concerns tab to flag items to manage.</Text>
          </View>
        )}
        {allManageConcerns.map(c => (
          <View key={c.mvarid} style={[styles.actionCard, { borderLeftColor: '#10b981' }]}>
            <TouchableOpacity
              onPress={() => onNavigateWithDomain?.('Concerns', getConcernDomainKey(c.mvarid))}
              activeOpacity={0.7}
            >
              <Text style={styles.actionTitle}>🛠️ {getGroupMVar('2', c.mvarid)?.text || getGroupMVar('3', c.mvarid)?.text || c.text}</Text>
            </TouchableOpacity>
            {renderActionCheckboxes('2', c.mvarid)}
          </View>
        ))}
        {orphanedManage.length > 0 && (
          <View style={styles.orphanedSection}>
            <Text style={styles.orphanedSectionTitle}>Deactivated concerns</Text>
            {orphanedManage.map(a => {
              const ck = `${a.group}|${a.mvar}|${a.action}`;
              const at = getActionType(a.action);
              const isCompletingThis = completingAction === ck;
              return (
                <View key={ck} style={[styles.actionCard, styles.orphanedCard]}>
                  <Text style={styles.deselectedLabel}>{getActionName(a.action)} — {concernTextMap.get(a.mvar) ?? a.mvar}</Text>
                  <View style={styles.completionBtnsRow}>
                    <TouchableOpacity style={styles.completionMarkBtn} onPress={() => setCompletingAction(isCompletingThis ? null : ck)} activeOpacity={0.75}>
                      <Text style={styles.completionMarkBtnText}>Mark complete</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.completionDeleteBtn} onPress={() => deleteAction(a.group, a.mvar, a.action)} activeOpacity={0.75}>
                      <Text style={styles.completionDeleteBtnText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                  {isCompletingThis && at && at.outcome.length > 0 && (
                    <View style={styles.outcomeRow}>
                      {at.outcome.map(o => (
                        <TouchableOpacity key={o.value} style={styles.outcomeBtn} onPress={() => markActionComplete(a.group, a.mvar, a.action, o.value)} activeOpacity={0.75}>
                          <Text style={styles.outcomeBtnText}>{o.text}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                  {isCompletingThis && at && at.outcome.length === 0 && (
                    <TouchableOpacity style={styles.outcomeBtn} onPress={() => markActionComplete(a.group, a.mvar, a.action, '')} activeOpacity={0.75}>
                      <Text style={styles.outcomeBtnText}>Confirm complete</Text>
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  const renderAdvocateContent = () => {
    // Orphaned: actions for group '3' whose mvarid is NOT in current seekHelp list
    const orphanedAdvocate = actionsState.filter(a =>
      a.group === '3' &&
      a.status !== '-1' &&
      !seekHelpConcerns.some(c => c.mvarid.toLowerCase() === a.mvar.toLowerCase())
    );
    return (
      <View style={styles.section}>
        {seekHelpConcerns.length === 0 && orphanedAdvocate.length === 0 && (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No advocate concerns found. Visit the Concerns tab to flag items to seek help with.</Text>
          </View>
        )}
        {seekHelpConcerns.map(c => (
          <View key={c.mvarid} style={[styles.actionCard, { borderLeftColor: '#e74c3c' }]}>
            <TouchableOpacity
              onPress={() => onNavigateWithDomain?.('Concerns', getConcernDomainKey(c.mvarid))}
              activeOpacity={0.7}
            >
              <Text style={styles.actionTitle}>🫲 {getGroupMVar('3', c.mvarid)?.text || c.text}</Text>
            </TouchableOpacity>
            {renderActionCheckboxes('3', c.mvarid)}
          </View>
        ))}
        {orphanedAdvocate.length > 0 && (
          <View style={styles.orphanedSection}>
            <Text style={styles.orphanedSectionTitle}>Deactivated concerns</Text>
            {orphanedAdvocate.map(a => {
              const ck = `${a.group}|${a.mvar}|${a.action}`;
              const at = getActionType(a.action);
              const isCompletingThis = completingAction === ck;
              return (
                <View key={ck} style={[styles.actionCard, styles.orphanedCard]}>
                  <Text style={styles.deselectedLabel}>{getActionName(a.action)} — {concernTextMap.get(a.mvar) ?? a.mvar}</Text>
                  <View style={styles.completionBtnsRow}>
                    <TouchableOpacity style={styles.completionMarkBtn} onPress={() => setCompletingAction(isCompletingThis ? null : ck)} activeOpacity={0.75}>
                      <Text style={styles.completionMarkBtnText}>Mark complete</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.completionDeleteBtn} onPress={() => deleteAction(a.group, a.mvar, a.action)} activeOpacity={0.75}>
                      <Text style={styles.completionDeleteBtnText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                  {isCompletingThis && at && at.outcome.length > 0 && (
                    <View style={styles.outcomeRow}>
                      {at.outcome.map(o => (
                        <TouchableOpacity key={o.value} style={styles.outcomeBtn} onPress={() => markActionComplete(a.group, a.mvar, a.action, o.value)} activeOpacity={0.75}>
                          <Text style={styles.outcomeBtnText}>{o.text}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                  {isCompletingThis && at && at.outcome.length === 0 && (
                    <TouchableOpacity style={styles.outcomeBtn} onPress={() => markActionComplete(a.group, a.mvar, a.action, '')} activeOpacity={0.75}>
                      <Text style={styles.outcomeBtnText}>Confirm complete</Text>
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  const prescriptionEditorRef = useRef<PrescriptionListEditorHandle>(null);
  const referralEditorRef = useRef<ReferralListEditorHandle>(null);

  const renderProviderContent = () => {
    const mvars = getGroupAllMVars('4');
    const isPrescriptionProviderMVar = (m: ActionMVarDto): boolean => {
      const haystack = `${m.mvar ?? ''} ${m.text ?? ''}`.toLowerCase();
      return haystack.includes('prescription');
    };
    const isReferralProviderMVar = (m: ActionMVarDto): boolean => {
      const haystack = `${m.mvar ?? ''} ${m.text ?? ''}`.toLowerCase();
      return haystack.includes('referral');
    };
    const visibleProviderMVars = mvars.filter((m) => {
      if (trackPrescriptions && isPrescriptionProviderMVar(m)) return false;
      if (trackReferrals && isReferralProviderMVar(m)) return false;
      return true;
    });
    return (
      <View style={styles.section}>
        {/* Prescriptions management card */}
        {trackPrescriptions && (
        <View style={[styles.actionCard, { borderLeftColor: '#10b981' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={styles.actionTitle}>🩺 Manage Prescriptions</Text>
            <TouchableOpacity onPress={() => prescriptionEditorRef.current?.openAdd()}>
              <Text style={{ fontSize: 12, color: '#10b981', fontWeight: '500' }}>Add Prescription</Text>
            </TouchableOpacity>
          </View>
          <PrescriptionListEditor
            ref={prescriptionEditorRef}
            events={activeProfile?.prescriptiontrack?.events ?? []}
            onUpdate={(rxEvents: PrescriptionEventDto[]) => {
              if (!activeProfile) return;
              const syncResult = syncMedicalTrackActions({
                actionsstate: activeProfile.actionsstate,
                prescriptiontrack: { events: rxEvents },
                referraltrack: activeProfile.referraltrack,
                actionDef,
                source: actionDef?.source ?? '',
              });
              updateProfile(activeProfile.id, {
                prescriptiontrack: { events: syncResult.prescriptionEvents },
                referraltrack: { events: syncResult.referralEvents },
                actionsstate: syncResult.actionsstate,
              });
              markProfileDirty(activeProfile.id, 'Prescriptions updated');
              void syncProfileToBackend(activeProfile.id).catch((error: unknown) => {
                console.warn('⚠️ ActionsView: Prescription sync to backend failed:', error);
              });
            }}
          />
        </View>
        )}
        {/* Referrals management card */}
        {trackReferrals && (
        <View style={[styles.actionCard, { borderLeftColor: '#3b82f6' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={styles.actionTitle}>🩺 Manage Referrals</Text>
            <TouchableOpacity onPress={() => referralEditorRef.current?.openAdd()}>
              <Text style={{ fontSize: 12, color: '#3b82f6', fontWeight: '500' }}>Add Referral</Text>
            </TouchableOpacity>
          </View>
          <ReferralListEditor
            ref={referralEditorRef}
            events={activeProfile?.referraltrack?.events ?? []}
            onUpdate={(refEvents: ReferralEventDto[]) => {
              if (!activeProfile) return;
              const syncResult = syncMedicalTrackActions({
                actionsstate: activeProfile.actionsstate,
                prescriptiontrack: activeProfile.prescriptiontrack,
                referraltrack: { events: refEvents },
                actionDef,
                source: actionDef?.source ?? '',
              });
              updateProfile(activeProfile.id, {
                prescriptiontrack: { events: syncResult.prescriptionEvents },
                referraltrack: { events: syncResult.referralEvents },
                actionsstate: syncResult.actionsstate,
              });
              markProfileDirty(activeProfile.id, 'Referrals updated');
              void syncProfileToBackend(activeProfile.id).catch((error: unknown) => {
                console.warn('⚠️ ActionsView: Referral sync to backend failed:', error);
              });
            }}
          />
        </View>
        )}
        {visibleProviderMVars.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No provider actions defined yet.</Text>
          </View>
        ) : (
          visibleProviderMVars.map(m => (
            <View key={m.mvar} style={[styles.actionCard, { borderLeftColor: '#6366f1' }]}>
              <Text style={styles.actionTitle}>🩺 {m.text || m.mvar}</Text>
              {renderActionCheckboxes('4', m.mvar)}
            </View>
          ))
        )}
      </View>
    );
  };

  const renderContent = () => {
    switch (selectedTab.key) {
      case 'Goals':    return renderGoalsContent();
      case 'Provider': return renderProviderContent();
      case 'Manage':   return renderManageContent();
      case 'Advocate': return renderAdvocateContent();
      case 'Summary':  return renderSummaryContent();
    }
  };

  return (
    <View style={[styles.backgroundContainer, { backgroundColor: appConfig.getAppBackgroundColor() }]}>
      <DatePickerModal
        visible={datePickerTarget !== null}
        title="Select date"
        initialValue={pickerInitialValue}
        accentColor="#2563eb"
        onCancel={closeDatePicker}
        onConfirm={(dateValue) => {
          if (!datePickerTarget) return;
          updateActionField(datePickerTarget.group, datePickerTarget.mvarid, datePickerTarget.actionId, 'dueDate', dateValue);
          closeDatePicker();
        }}
      />

      {/* Fixed Header with Navigation Controls - full width background, constrained content */}
      <View style={styles.fixedDomainHeader}>
        <View style={{ maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }}>
        {/* Tab name row */}
        <View style={styles.domainTitleRow}>
          <Text style={styles.domainTitle}>
            {selectedTab.icon}  {selectedTab.headerTitle ?? selectedTab.name}
          </Text>
          {showBackToSnapshot && onBackToSnapshot ? (
            <TouchableOpacity style={styles.backToSnapshotBtn} onPress={onBackToSnapshot}>
              <Text style={styles.backToSnapshotBtnText}>Back to Snapshot ▶</Text>
            </TouchableOpacity>
          ) : selectedTabIndex === ACTION_TABS.length - 1 && onHomeDashboard ? (
            <TouchableOpacity style={styles.backToSnapshotBtn} onPress={handleHomeDashboard}>
              <Text style={styles.backToSnapshotBtnText}>Home Dashboard ▶</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        {/* Navigation Controls Row */}
        <View style={styles.navigationRow}>
          <View style={styles.leftNavigationControls} />

          <View style={styles.centerDescription}>
            <Text style={styles.domainDescription}>{selectedTab.description}</Text>
            {showLastUpdate && tabLastUpdated ? (
              <Text style={styles.domainStatusInfo}>
                {'🕐 Updated: '}{formatComputationTimestamp(tabLastUpdated)}
              </Text>
            ) : tabStaleness.isStale ? (
              <Text style={styles.domainStatusWarning}>{'⚠️ Needs review'}</Text>
            ) : tabLastUpdated === null ? (
              <Text style={styles.domainStatusInfo}>{'📋 Not yet reviewed'}</Text>
            ) : null}
          </View>

          <View style={styles.rightNavigationControls}>
            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedTabIndex === 0 && styles.navButtonDisabled]}
              onPress={handlePrevTab}
              disabled={selectedTabIndex === 0}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>◀</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedTabIndex === ACTION_TABS.length - 1 && styles.navButtonDisabled]}
              onPress={handleNextTab}
              disabled={selectedTabIndex === ACTION_TABS.length - 1}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>▶</Text>
            </TouchableOpacity>
          </View>
        </View>
        </View>
      </View>

      {/* Tab Menu - full width background, content constrained to headerWidthMax */}
      <View style={styles.tabSectionOuter}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[styles.tabContainer, { maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }]}
          contentContainerStyle={styles.tabContent}
        >
        {ACTION_TABS.map((tab, index) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              selectedTabIndex === index && { backgroundColor: appConfig.getNavButtonColor() }
            ]}
            onPress={() => navigateToTab(index)}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Text style={styles.tabIcon}>{tab.icon}</Text>
              <Text style={[
                styles.tabText,
                selectedTabIndex === index && styles.selectedTabText
              ]}>
                {tab.name}
              </Text>
            </View>
          </TouchableOpacity>
        ))}
        </ScrollView>
      </View>

      {/* Spacer between tab bar and content card */}
      <View style={{ height: 12, backgroundColor: appConfig.getHeaderBackgroundColor() }} />

      {/* Main Content Card */}
      <View style={styles.cardWrapper}>
        <View style={[styles.container, { maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%' }]}>
          <ScrollView style={styles.contentContainer}>
            {renderContent()}
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  cardWrapper: {
    flex: 1,
    paddingHorizontal: appConfig.getCardMargin(),
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  header: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  tabSectionOuter: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
  },
  tabContainer: {
    maxHeight: 60,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'center',
  },
  tab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#f1f3f4',
    minWidth: 100,
    alignItems: 'center',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  tabIcon: {
    fontSize: 15,
  },
  selectedTabText: {
    color: '#ffffff',
  },
  fixedDomainHeader: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    paddingTop: 16 + appConfig.getMobileHeaderTopPadding(),
    paddingBottom: 16,
    paddingHorizontal: 24,
  },
  domainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  domainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  backToSnapshotBtn: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 12,
    flexShrink: 0,
  },
  backToSnapshotBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  navigationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftNavigationControls: {
    flexDirection: 'row',
    gap: 8,
    minWidth: 36,
  },
  rightNavigationControls: {
    flexDirection: 'row',
    gap: 8,
  },
  centerDescription: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  domainDescription: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  domainStatusInfo: {
    fontSize: 11,
    color: '#cbd5e1',
    textAlign: 'center',
    marginTop: 4,
    opacity: 0.9,
  },
  domainStatusWarning: {
    fontSize: 11,
    color: '#fde68a',
    textAlign: 'center',
    marginTop: 4,
    fontWeight: '600',
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  navButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
    padding: 16,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 12,
  },
  actionCard: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    borderLeftWidth: 4,
    borderLeftColor: '#e74c3c',
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 4,
  },
  actionDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 8,
    lineHeight: 20,
  },
  actionDue: {
    fontSize: 13,
    color: '#e67e22',
    marginBottom: 4,
    fontWeight: '500',
  },
  actionStatus: {
    fontSize: 13,
    color: '#f39c12',
    fontWeight: '500',
  },
  completedCard: {
    backgroundColor: '#f0f8f0',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#27ae60',
  },
  completedTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#27ae60',
    marginBottom: 4,
  },
  completedDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 8,
  },
  completedDate: {
    fontSize: 12,
    color: '#27ae60',
    fontWeight: '500',
  },
  addActionContainer: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: '#e8f5e8',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#27ae60',
    borderStyle: 'dashed',
    alignItems: 'center',
  },
  addActionText: {
    fontSize: 16,
    color: '#27ae60',
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  // ── ActionDef checkbox list (inside concern cards) ──────────────────────
  actionCheckList: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 8,
  },
  // Outer row: checkbox+label touchable on the left, selector on the right
  actionCheckRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    gap: 8,
  },
  actionQuickLinkBtn: {
    marginLeft: 'auto',
    paddingVertical: 2,
    paddingHorizontal: 0,
  },
  actionQuickLinkText: {
    fontSize: 13,
    color: '#2563eb',
    fontWeight: '500' as const,
  },
  // Touchable area covering just the checkbox + label
  actionCheckTouchable: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    gap: 10,
  },
  actionCheckBox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#94a3b8',
    backgroundColor: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionCheckBoxChecked: {
    borderColor: '#10b981',
    backgroundColor: '#10b981',
  },
  actionCheckMark: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 'bold' as const,
    lineHeight: 16,
  },
  actionCheckLabel: {
    fontSize: 14,
    color: '#475569',
    flexShrink: 1,
  },
  actionCheckLabelEnabled: {
    color: '#1e293b',
    fontWeight: '600' as const,
  },
  // ── Inline schedule selector (right side of action row) ──────────────────
  scheduleSelectorBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    paddingVertical: 3,
    paddingHorizontal: 7,
    gap: 3,
  },
  scheduleSelectorBtnDisabled: {
    backgroundColor: '#f8fafc',
    borderColor: '#e2e8f0',
    opacity: 0.55,
  },
  scheduleValue: {
    fontSize: 14,
    color: '#1e293b',
  },
  scheduleValueDisabled: {
    color: '#94a3b8',
  },
  scheduleArrow: {
    fontSize: 16,
    color: '#64748b',
    lineHeight: 18,
    fontWeight: '300' as const,
  },
  scheduleArrowDisabled: {
    color: '#c0cfe0',
  },
  // Date event inline container
  scheduleInline: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    paddingVertical: 3,
    paddingHorizontal: 7,
  },
  scheduleInlineDisabled: {
    backgroundColor: '#f8fafc',
    borderColor: '#e2e8f0',
    opacity: 0.55,
  },
  scheduleDateButton: {
    minWidth: 120,
    maxWidth: 160,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    backgroundColor: '#ffffff',
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  scheduleDateButtonDisabled: {
    backgroundColor: '#f8fafc',
    borderColor: '#e2e8f0',
  },
  scheduleDateButtonText: {
    fontSize: 14,
    color: '#1e293b',
    textAlign: 'center',
  },
  scheduleDateButtonTextDisabled: {
    color: '#94a3b8',
  },
  // ── Completion / deselected UI ────────────────────────────────────────────
  deselectedBlock: {
    flex: 1,
    paddingVertical: 4,
  },
  deselectedLabel: {
    fontSize: 13,
    color: '#94a3b8',
    fontStyle: 'italic' as const,
    marginBottom: 4,
  },
  completionBtnsRow: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: 6,
    marginTop: 2,
    marginBottom: 4,
  },
  completionMarkBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#059669',
    borderRadius: 6,
  },
  completionMarkBtnText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600' as const,
  },
  completionDeleteBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#ef4444',
    borderRadius: 6,
  },
  completionDeleteBtnText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600' as const,
  },
  completionReEnableBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#64748b',
    borderRadius: 6,
  },
  completionReEnableBtnText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600' as const,
  },
  outcomeRow: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: 6,
    marginTop: 4,
  },
  outcomeBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#f1f5f9',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
  },
  outcomeBtnText: {
    fontSize: 12,
    color: '#1e293b',
  },
  // ── Orphaned actions ──────────────────────────────────────────────────────
  orphanedSection: {
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 8,
  },
  orphanedSectionTitle: {
    fontSize: 12,
    color: '#94a3b8',
    fontWeight: '600' as const,
    marginBottom: 6,
    textTransform: 'uppercase' as const,
  },
  orphanedCard: {
    borderLeftColor: '#94a3b8',
    backgroundColor: '#f8fafc',
  },
});